import { queryClient } from "@shared/lib/queryClient";
import { wsClient } from "@shared/services/ws/wsClient";
import { setCSRFToken } from "@shared/store/csrfStore";
import { create } from "zustand";
import type { User } from "../../domain/types";
import { authContainer } from "../../infrastructure/di/container";

interface AuthState {
	user: User | null;
	isLoading: boolean;
	isAuthenticated: boolean;
	/** Último error de `login` o `logout` (no-401). Null si no hay error. */
	lastError: string | null;

	login: (email: string, password: string) => Promise<void>;
	logout: () => Promise<void>;
	checkAuth: () => Promise<void>;
	changePassword: (
		currentPassword: string,
		newPassword: string,
	) => Promise<{ message: string }>;
	clearError: () => void;
}

let checkAuthPromise: Promise<void> | null = null;

export const useAuthStore = create<AuthState>((set) => ({
	user: null,
	isLoading: true, // true until checkAuth completes — gates ProtectedRoute
	isAuthenticated: false,
	lastError: null,

	login: async (email, password) => {
		set({ lastError: null });
		try {
			const result = await authContainer.repository.login({ email, password });
			setCSRFToken(result.csrf_token);
			set({ user: result.user, isAuthenticated: true, isLoading: false });
			wsClient.connect();
		} catch (err) {
			set({
				lastError:
					(err as { message?: string })?.message ||
					"Error al iniciar sesión",
				isLoading: false,
			});
			throw err;
		}
	},

	logout: async () => {
		try {
			await authContainer.repository.logout();
		} catch (err) {
			// 401 (sesión ya inválida en backend) se ignora: el frontend debe
			// limpiar su estado local de todos modos. Otros errores se guardan
			// en `lastError` para que el caller pueda mostrarlos.
			const status = (err as { status?: number })?.status;
			if (status !== 401) {
				set({
					lastError:
						(err as { message?: string })?.message ||
						"Error al cerrar sesión",
				});
			}
		}
		setCSRFToken(null);
		wsClient.disconnect();
		queryClient.clear();
		set({ user: null, isAuthenticated: false, isLoading: false });
	},

	checkAuth: async () => {
		if (checkAuthPromise) {
			return checkAuthPromise;
		}

		set({ isLoading: true });
		checkAuthPromise = (async () => {
			try {
				// Una sola vía de refresh: `apiClient` ya reintenta con
				// /auth/refresh ante un 401. Hacer un refresh manual aquí ADEMÁS
				// del retry interno consume dos veces un refresh token rotable
				// (de un solo uso) y el segundo falla → flasheo login/dashboard.
				const user = await authContainer.repository.me();
				set({ user, isAuthenticated: true, isLoading: false });
				wsClient.connect();
			} catch {
				setCSRFToken(null);
				set({ user: null, isAuthenticated: false, isLoading: false });
			} finally {
				checkAuthPromise = null;
			}
		})();

		return checkAuthPromise;
	},

	changePassword: async (currentPassword, newPassword) => {
		const result = await authContainer.repository.changePassword({
			current_password: currentPassword,
			new_password: newPassword,
		});
		// El backend revoca todos los refresh tokens y limpia cookies.
		// Limpiamos estado local y cerramos WS.
		setCSRFToken(null);
		wsClient.disconnect();
		set({ user: null, isAuthenticated: false, isLoading: false });
		return result;
	},

	clearError: () => set({ lastError: null }),
}));

// Sesión definitivamente muerta (refresh falló en apiClient): limpiar estado
// local UNA vez. `ProtectedRoute` hace el redirect a /login sin flasheos.
// Se guarda el flag para no limpiar dos veces ante ráfagas de 401.
if (typeof window !== "undefined") {
	window.addEventListener("auth:session-expired", () => {
		const { isAuthenticated, user } = useAuthStore.getState();
		if (!isAuthenticated && !user) return;
		setCSRFToken(null);
		wsClient.disconnect();
		queryClient.clear();
		useAuthStore.setState({
			user: null,
			isAuthenticated: false,
			isLoading: false,
		});
	});
}
